/**
 * The tile pyramid's numbers and the pieces every export shares: the row-to-tile cut, the whole-map
 * fallback and the card picture. What fills a row (the painter of a track, or a picture loaded from
 * a file) is the caller's own business; the pyramid stays the same.
 *
 * Output paths are relative to the game's asset root: `tiles/<id>/<level>/<x>_<y>.webp` and
 * `tiles/<id>/base.webp`, with `manifest` going into the track's JSON as `backgroundTiles`.
 *
 * Every tile is baked with a 1-texel GUTTER of the neighbouring content around its visible square,
 * and the game draws only that inner square (a texture frame): two tiles are separate textures, and
 * without the gutter the bilinear filter at the shared edge has no neighbour texel to read, which
 * shows up as a thin line along the seam.
 */
export const TILE_PX = 512;
export const TILE_GUTTER = 1;            // texels of neighbour content baked around each tile
export const TILE_LEVELS = [2, 1, 0.5];   // px per world unit, finest first
export const BASE_SCALE = 0.25;           // px per world unit of the whole-map fallback

/**
 * The whole-map thumbnail the track picker shows, beside the tile folders: it is not a tile, it is
 * the one picture a player sees in a list. `PREVIEW_MAX_PX` is the longest side — a card is 130 CSS
 * px tall, so this is crisp at 2x and still a few tens of KB, where the full picture was the map's
 * own resolution.
 */
export const PREVIEW_MAX_PX = 512;

/** The `backgroundTiles` block a track file carries (see the game's TrackTiles). */
export function tileManifest(track) {
  const dir = `tiles/${track.id ?? 'track_custom'}`;
  return {
    dir,
    tilePx: TILE_PX,
    gutter: TILE_GUTTER,
    levels: TILE_LEVELS.slice(),
    base: `${dir}/base.webp`,
  };
}

export function tilePreviewPath(track) {
  return `preview/${track.id ?? 'track_custom'}.webp`;
}

/** The bare road silhouette, which the track picker shows in place of the painted preview. */
export function blackPicturePath(track) {
  return `black/${track.id ?? 'track_custom'}.webp`;
}

/**
 * The size of a track's card picture: the whole map at BASE_SCALE, longest side capped to
 * PREVIEW_MAX_PX. Both card pictures are drawn at this size, so either one can stand in for the other.
 */
export function previewSize(width, height) {
  const baseWidth = Math.max(1, Math.ceil(width * BASE_SCALE));
  const baseHeight = Math.max(1, Math.ceil(height * BASE_SCALE));
  const scale = Math.min(1, PREVIEW_MAX_PX / Math.max(baseWidth, baseHeight));
  return {
    width: Math.max(1, Math.round(baseWidth * scale)),
    height: Math.max(1, Math.round(baseHeight * scale)),
  };
}

/** A track's card picture, drawn from any picture of the whole map. */
export function drawPreview(source, track, createCanvas) {
  const { width, height } = previewSize(track.width, track.height);
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

/**
 * Cuts one filled row canvas into its tiles: `canvas` holds the row's content between two gutter
 * columns, `rowIndex` is its position down the level.
 *
 * @returns {Promise<number>} how many tiles the row produced
 */
export async function cutRowIntoTiles({ canvas, level, rowIndex, id, createCanvas, encode, files }) {
  const contentWidth = canvas.width - TILE_GUTTER * 2;
  const columns = Math.ceil(contentWidth / TILE_PX);
  const outSize = TILE_PX + TILE_GUTTER * 2;
  for (let x = 0; x < columns; x++) {
    const tile = createCanvas(outSize, outSize);
    const ctx = tile.getContext('2d');
    const sx = x * TILE_PX;
    const sw = Math.min(outSize, canvas.width - sx);
    const sh = canvas.height;
    // The last column/row of the map is short: the rest of the tile stays transparent, and the
    // band painted around the map shows through it exactly as it does on screen.
    ctx.drawImage(canvas, sx, 0, sw, sh, 0, 0, sw, sh);
    files.push({ path: `tiles/${id}/${level}/${x}_${rowIndex}.webp`, data: await encode(tile, 0.9) });
  }
  return columns;
}

/** Writes the whole-map fallback and the card picture made from it. */
export async function writeBasePicture({ base, track, createCanvas, encode, files }) {
  files.push({ path: `tiles/${track.id ?? 'track_custom'}/base.webp`, data: await encode(base, 0.9) });

  const preview = drawPreview(base, track, createCanvas);
  files.push({ path: tilePreviewPath(track), data: await encode(preview, 0.8) });
  releaseCanvas(preview);
}

/**
 * Encodes a canvas as WebP. Tiles are fetched from a fixed `.webp` name, so there is no PNG
 * fallback here: a browser that cannot write WebP says so, and the editor's single-picture export
 * (which does fall back) stays available.
 */
export function encodeWebp(canvas, quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('canvas.toBlob returned nothing'));
      if (blob.type !== 'image/webp') return reject(new Error('this browser cannot write WebP'));
      blob.arrayBuffer().then((buffer) => resolve(new Uint8Array(buffer)), reject);
    }, 'image/webp', quality);
  });
}

/**
 * Drops a canvas' backing store now instead of whenever the collector gets to it: during an export
 * several 16 Mpx canvases come and go, and the browser has to have the memory before allocating the
 * next one.
 */
export function releaseCanvas(canvas) {
  canvas.width = 1;
  canvas.height = 1;
}

export function createDefaultCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
